/**
 * Simulator Labs — The Brain of ProfitSpot
 * Merged IL Calculator + HODL Benchmark + Advanced Backtesting (Unlimited)
 */
import React, { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  FlaskConical, TrendingUp, AlertTriangle, Info,
  DollarSign, Percent, Clock, RefreshCw, Crown,
  ChevronDown, Unlock, BarChart3, Target, Brain, Cpu,
  TrendingDown, Minus, Sparkles, Search, X, Link2
} from 'lucide-react';

import UpgradeTeaser from '../components/UpgradeTeaser';
import { FeatureInfo } from '../components/FeatureInfo';
import { getBackendBaseUrl } from '../lib/apiBase';

const API_URL = getBackendBaseUrl();

/** Pool autocomplete menu at document.body — avoids z-index / overflow clipping inside cards */
function PoolSearchDropdownPortal({ open, anchorRef, children, maxHeightClass = 'max-h-52' }) {
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!open || !anchorRef?.current) {
      setPos(null);
      return;
    }
    const el = anchorRef.current;
    const update = () => {
      const r = el.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef]);
  if (!open || !pos) return null;
  return createPortal(
    <div
      className={`fixed z-[9998] bg-[#1a1a2e] border border-gray-700 rounded-lg shadow-2xl ${maxHeightClass} overflow-y-auto overflow-x-hidden`}
      style={{ top: pos.top, left: pos.left, width: Math.max(pos.width, 220) }}
      role="listbox"
    >
      {children}
    </div>,
    document.body
  );
}

// ─── Dual-Line Area Chart with HODL Benchmark ────────────────
const SimulatorChart = ({ data, hodlValue, crossoverDay, showHodl = true }) => {
  if (!data || data.length === 0) return null;

  const width = 100;
  const height = 55;
  const padding = { top: 8, right: 5, bottom: 14, left: 5 };
  const chartW = width - padding.left - padding.right;
  const chartH = height - padding.top - padding.bottom;

  // Calculate scales - include HODL value which can be negative
  const allValues = data.flatMap(d => [d.cumulative_yield, d.total_costs]);
  if (showHodl && hodlValue !== undefined) {
    allValues.push(hodlValue);
  }
  
  const maxY = Math.max(...allValues) * 1.1;
  const minY = Math.min(...allValues, 0) * 1.1; // Allow negative values
  const range = maxY - minY;
  
  const xScale = (i) => padding.left + (i / (data.length - 1)) * chartW;
  const yScale = (v) => padding.top + chartH - ((v - minY) / range) * chartH;

  // Generate paths
  const yieldPath = data.map((d, i) => `${i === 0 ? 'M' : 'L'} ${xScale(i)} ${yScale(d.cumulative_yield)}`).join(' ');
  const yieldAreaPath = `${yieldPath} L ${xScale(data.length - 1)} ${yScale(0)} L ${xScale(0)} ${yScale(0)} Z`;
  
  const costsY = yScale(data[0]?.total_costs || 0);
  const costsAreaPath = `M ${padding.left} ${costsY} L ${padding.left + chartW} ${costsY} L ${padding.left + chartW} ${yScale(0)} L ${padding.left} ${yScale(0)} Z`;

  // HODL line (dashed)
  const hodlY = yScale(hodlValue || 0);

  // Find crossover point
  const crossoverIndex = data.findIndex(d => d.break_even_reached);
  const crossoverX = crossoverIndex >= 0 ? xScale(crossoverIndex) : null;

  // Check if yield is below HODL (Negative Alpha)
  const lastYield = data[data.length - 1]?.cumulative_yield || 0;
  const isNegativeAlpha = showHodl && hodlValue !== undefined && lastYield < hodlValue;

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="xMidYMid meet" className="w-full h-64">
        <defs>
          <linearGradient id="sim-gradient-yield" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#00ff88" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#00ff88" stopOpacity="0.05" />
          </linearGradient>
          <linearGradient id="sim-gradient-costs" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ffa502" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#ffa502" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <line key={i} x1={padding.left} y1={padding.top + chartH * (1 - pct)} x2={padding.left + chartW} y2={padding.top + chartH * (1 - pct)} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
        ))}

        {/* Costs area (flat) */}
        <path d={costsAreaPath} fill="url(#sim-gradient-costs)" />
        <line x1={padding.left} y1={costsY} x2={padding.left + chartW} y2={costsY} stroke="#ffa502" strokeWidth="1.5" />

        {/* Yield area (growing) */}
        <path d={yieldAreaPath} fill="url(#sim-gradient-yield)" />
        <path d={yieldPath} fill="none" stroke="#00ff88" strokeWidth="1.5" />

        {/* HODL benchmark line (dashed) - can be above or below zero */}
        {showHodl && hodlValue !== undefined && (
          <>
            <line 
              x1={padding.left} y1={hodlY} 
              x2={padding.left + chartW} y2={hodlY} 
              stroke="#00d4ff" strokeWidth="1" strokeDasharray="3 2" opacity="0.7"
            />
            <text x={padding.left + chartW - 1} y={hodlY - 1.5} fill="#00d4ff" fontSize="3" textAnchor="end">
              HODL {hodlValue < 0 ? '(Loss)' : ''}
            </text>
          </>
        )}

        {/* Crossover marker */}
        {crossoverX && (
          <>
            <line x1={crossoverX} y1={padding.top} x2={crossoverX} y2={padding.top + chartH} stroke="#ffa502" strokeWidth="0.5" strokeDasharray="2 2" opacity="0.6" />
            <circle cx={crossoverX} cy={costsY} r="1.5" fill="#ffa502" stroke="#fff" strokeWidth="0.5" />
          </>
        )}

        {/* X-axis labels */}
        <text x={padding.left} y={height - 3} fill="rgba(255,255,255,0.4)" fontSize="3">Day 1</text>
        <text x={padding.left + chartW / 2} y={height - 3} fill="rgba(255,255,255,0.4)" fontSize="3" textAnchor="middle">{Math.floor(data.length / 2)}d</text>
        <text x={padding.left + chartW} y={height - 3} fill="rgba(255,255,255,0.4)" fontSize="3" textAnchor="end">{data.length}d</text>
      </svg>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-4 mt-2 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-[#ffa502]"></div>
          <span className="text-gray-400">Total Costs (Gas+Slippage)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-[#00ff88]"></div>
          <span className="text-gray-400">Net Yield</span>
        </div>
        {showHodl && (
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-0.5 bg-[#00d4ff]" style={{ borderStyle: 'dashed' }}></div>
            <span className="text-gray-400">HODL Benchmark</span>
          </div>
        )}
        {crossoverDay > 0 && (
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[#ffa502]"></div>
            <span className="text-[#ffa502] font-medium">
              Break-even: {crossoverDay < 1 ? `${(crossoverDay * 24).toFixed(1)}h` : `${crossoverDay.toFixed(1)}d`}
            </span>
          </div>
        )}
      </div>

      {/* Negative Alpha Warning */}
      {isNegativeAlpha && (
        <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2" data-testid="negative-alpha-warning">
          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
          <div>
            <p className="text-sm font-bold text-red-400">Negative Alpha Warning</p>
            <p className="text-xs text-red-300/80">Your projected yield is below HODL returns. Consider different strategies.</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Price Divergence Slider ─────────────────────────────────
const PriceDivergenceSlider = ({ value, onChange, disabled }) => {
  const getLabel = (val) => {
    if (val === 0) return 'No Change';
    if (val > 0) return `+${val}% divergence`;
    return `${val}% divergence`;
  };

  const getImpactColor = (val) => {
    const absVal = Math.abs(val);
    if (absVal <= 10) return 'text-green-400';
    if (absVal <= 30) return 'text-yellow-400';
    return 'text-red-400';
  };

  return (
    <div className={`p-4 bg-gray-800/30 rounded-lg ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <label className="text-sm text-gray-400 flex items-center gap-2">
          <Percent className="w-4 h-4" />
          Price Divergence (IL Simulation)
        </label>
        <span className={`text-sm font-bold ${getImpactColor(value)}`}>
          {getLabel(value)}
        </span>
      </div>
      <input 
        type="range" 
        min="-80" 
        max="80" 
        step="5"
        value={value} 
        onChange={(e) => onChange(Number(e.target.value))}
        disabled={disabled}
        className="w-full accent-[#00ff88] cursor-pointer disabled:cursor-not-allowed"
        data-testid="price-divergence-slider"
      />
      <div className="flex justify-between text-[10px] text-gray-600 mt-1">
        <span>-80%</span>
        <span>0%</span>
        <span>+80%</span>
      </div>
      <p className="text-[10px] text-gray-500 mt-2">
        Simulates how price changes between assets affect your Impermanent Loss
      </p>
    </div>
  );
};

// ─── Premium Gating Overlay ──────────────────────────────────
const PremiumOverlay = ({ feature, onUnlock }) => (
  <div className="absolute inset-0 bg-black/40 backdrop-blur-[6px] rounded-lg flex items-center justify-center z-[10000] overflow-visible">
    <div className="text-center p-6 bg-[#0f0f1a]/90 rounded-xl border border-gray-700 shadow-2xl max-w-xs mx-4">
      <Crown className="w-10 h-10 text-[#ffa502] mx-auto mb-3" />
      <p className="text-base font-bold text-white mb-1">{feature}</p>
      <p className="text-xs text-gray-400 mb-1">See a blurred preview below</p>
      <p className="text-xs text-gray-500 mb-4">Upgrade to unlock full access with live data</p>
      <button 
        onClick={onUnlock}
        className="w-full px-5 py-2.5 bg-[#00ff88] text-black font-bold text-sm rounded-lg hover:bg-[#00cc6a] transition-colors"
        data-testid="unlock-feature-btn"
      >
        <Unlock className="w-4 h-4 inline mr-1" /> Unlock Pro — $29/mo
      </button>
      <p className="text-[10px] text-gray-600 mt-2">7-day free trial on Pro ($29/mo)</p>
    </div>
  </div>
);

// ─── Smart Exit Timer ────────────────────────────────────────
// CoinGecko token → ID map (free public API, no key needed)
const COINGECKO_IDS = {
  ETH: 'ethereum', BTC: 'bitcoin', SOL: 'solana', ARB: 'arbitrum',
  OP: 'optimism', MATIC: 'matic-network', AVAX: 'avalanche-2', BNB: 'binancecoin',
  LINK: 'chainlink', UNI: 'uniswap', USDC: 'usd-coin', USDT: 'tether', DAI: 'dai', FRAX: 'frax',
};
const STABLECOINS_SET = new Set(['USDC', 'USDT', 'DAI', 'FRAX', 'BUSD', 'LUSD', 'crvUSD', 'GUSD']);

const fetchLivePrice = async (symbol) => {
  const id = COINGECKO_IDS[symbol?.toUpperCase()];
  if (!id) return null;
  try {
    const res = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${id}&vs_currencies=usd`, { cache: 'no-store' });
    if (res.ok) { const d = await res.json(); return d[id]?.usd ?? null; }
  } catch { return null; }
  return null;
};

const SmartExitTimer = ({ user, isAuthenticated }) => {
  const today = new Date().toISOString().split('T')[0];
  const userTier = user?.subscription?.tier || 'free';
  const isAdmin = user?.role === 'admin';
  const canAccess = isAuthenticated && (userTier !== 'free' || isAdmin);

  const [entryDate, setEntryDate] = useState('');
  const [depositAmount, setDepositAmount] = useState(5000);
  const [poolApy, setPoolApy] = useState(45);
  const [result, setResult] = useState(null);
  const [fetchingPrice, setFetchingPrice] = useState(null);

  // Dynamic token list — supports 2-5 tokens (for 3pool, 4pool, etc.)
  const [tokens, setTokens] = useState([
    { symbol: 'ETH', entryPrice: '', currentPrice: '' },
    { symbol: 'USDC', entryPrice: '', currentPrice: '' },
  ]);

  const COMMON_TOKENS = ['ETH', 'BTC', 'SOL', 'ARB', 'OP', 'MATIC', 'AVAX', 'BNB', 'LINK', 'UNI'];
  const STABLE_TOKENS = ['USDC', 'USDT', 'DAI', 'FRAX'];
  const ALL_TOKENS = [...new Set([...COMMON_TOKENS, ...STABLE_TOKENS])];
  const MAX_TOKENS = 5;

  const updateToken = (index, field, value) => {
    setTokens(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  };

  const addToken = () => {
    if (tokens.length >= MAX_TOKENS) { toast.error(`Maximum ${MAX_TOKENS} tokens supported`); return; }
    const usedSymbols = new Set(tokens.map(t => t.symbol));
    const nextSymbol = ALL_TOKENS.find(t => !usedSymbols.has(t)) || 'DAI';
    setTokens(prev => [...prev, { symbol: nextSymbol, entryPrice: '', currentPrice: '' }]);
  };

  const removeToken = (index) => {
    if (tokens.length <= 2) { toast.error('Need at least 2 tokens'); return; }
    setTokens(prev => prev.filter((_, i) => i !== index));
  };

  const getLivePrice = async (tokenSymbol, index) => {
    if (STABLECOINS_SET.has(tokenSymbol.toUpperCase())) {
      updateToken(index, 'currentPrice', '1.00');
      toast.success(`${tokenSymbol} is a stablecoin — price set to $1.00`);
      return;
    }
    setFetchingPrice(index);
    const price = await fetchLivePrice(tokenSymbol);
    if (price) {
      updateToken(index, 'currentPrice', String(price));
      toast.success(`${tokenSymbol} live price: $${price.toLocaleString()}`);
    } else {
      toast.error(`Could not fetch live price for ${tokenSymbol} — enter manually`);
    }
    setFetchingPrice(null);
  };

  const checkStablecoin = (symbol, price) => {
    if (STABLECOINS_SET.has(symbol.toUpperCase()) && Math.abs(parseFloat(price) - 1.0) > 0.05) {
      toast.warning(`${symbol} is a stablecoin pegged to $1.00. Your entered price ($${price}) looks unusual — is that right?`, { duration: 6000 });
    }
  };

  const resetCalculator = () => {
    setResult(null);
    setEntryDate('');
    setDepositAmount(5000);
    setPoolApy(45);
    setTokens([
      { symbol: 'ETH', entryPrice: '', currentPrice: '' },
      { symbol: 'USDC', entryPrice: '', currentPrice: '' },
    ]);
  };

  const calculate = () => {
    if (!entryDate) { toast.error('Select your entry date'); return; }

    const N = tokens.length;
    const parsed = tokens.map(t => ({
      symbol: t.symbol,
      entry: parseFloat(t.entryPrice),
      current: parseFloat(t.currentPrice),
    }));

    // Validate all prices filled
    const missing = parsed.some(p => !p.entry || !p.current);
    if (missing) { toast.error(`Enter all ${N * 2} prices (entry + current for each token)`); return; }

    // Stablecoin sanity checks
    tokens.forEach(t => checkStablecoin(t.symbol, t.currentPrice));

    const msPerDay = 86400000;
    const daysHeld = Math.max(1, Math.floor((new Date() - new Date(entryDate)) / msPerDay));

    // Generalized IL for N equal-weight tokens:
    // IL = N * geometric_mean(ratios) / sum(ratios) - 1
    // where ratio_i = currentPrice_i / entryPrice_i
    const ratios = parsed.map(p => p.current / p.entry);
    const sumRatios = ratios.reduce((a, b) => a + b, 0);
    const geometricMean = Math.pow(ratios.reduce((a, b) => a * b, 1), 1 / N);
    const ilPct = (N * geometricMean / sumRatios - 1) * 100;
    const ilAbs = Math.abs(ilPct);
    const ilUSD = depositAmount * (ilAbs / 100);

    // Yield
    const dailyYield = depositAmount * (poolApy / 365 / 100);
    const yieldEarned = dailyYield * daysHeld;
    const estimatedFees = depositAmount * 0.003;

    // Net LP P&L (vs holding cash)
    const netPnL = yieldEarned - ilUSD - estimatedFees;

    // HODL P&L — what if they had held equally outside the pool?
    const hodlTotal = ratios.reduce((sum, r) => sum + (depositAmount / N) * r, 0);
    const hodlPnL = hodlTotal - depositAmount;
    const opportunityCost = netPnL - hodlPnL;

    // Break-even day
    const breakEvenDay = dailyYield > 0 ? Math.ceil((ilUSD + estimatedFees) / dailyYield) : 999;

    const priceChangeA = ((parsed[0].current / parsed[0].entry) - 1) * 100;
    const isHighDivergence = Math.abs(priceChangeA) > 50;

    // Verdict
    let verdict, verdictColor, verdictBg, advice;
    if (netPnL < 0 && ilAbs > 15) {
      verdict = 'EXIT NOW';
      verdictColor = 'text-red-400';
      verdictBg = 'border-red-500/40 bg-red-500/5';
      advice = `IL (${ilAbs.toFixed(1)}%) has overtaken your yield. Every additional day risks deeper losses.`;
    } else if (netPnL < -estimatedFees * 0.5 && ilAbs > 8) {
      verdict = 'EXIT SOON';
      verdictColor = 'text-[#ffa502]';
      verdictBg = 'border-[#ffa502]/40 bg-[#ffa502]/5';
      advice = `You need ${Math.max(0, breakEvenDay - daysHeld)} more days of yield to break even at current IL.`;
    } else if (netPnL >= 0 && yieldEarned > ilUSD * 2) {
      verdict = 'HARVEST PROFITS';
      verdictColor = 'text-[#00ff88]';
      verdictBg = 'border-[#00ff88]/40 bg-[#00ff88]/5';
      advice = `Yield is ${(yieldEarned / Math.max(ilUSD, 1)).toFixed(1)}× your IL. Consider locking in the +$${netPnL.toFixed(0)} net LP gain.`;
    } else if (netPnL >= 0 && daysHeld < breakEvenDay + 14) {
      verdict = 'HOLD';
      verdictColor = 'text-[#00d4ff]';
      verdictBg = 'border-[#00d4ff]/40 bg-[#00d4ff]/5';
      advice = `Position is profitable. Let yield compound further before exiting.`;
    } else {
      verdict = 'MONITOR';
      verdictColor = 'text-[#00d4ff]';
      verdictBg = 'border-[#00d4ff]/40 bg-[#00d4ff]/5';
      advice = `${daysHeld > breakEvenDay ? 'Past break-even — well done!' : `${Math.max(0, breakEvenDay - daysHeld)} more days until break-even.`} Keep watching price divergence.`;
    }

    setResult({
      daysHeld, ilPct: ilPct.toFixed(2), ilAbs: ilAbs.toFixed(2), ilUSD: ilUSD.toFixed(2),
      yieldEarned: yieldEarned.toFixed(2), netPnL: netPnL.toFixed(2),
      hodlPnL: hodlPnL.toFixed(2), opportunityCost: opportunityCost.toFixed(2),
      isHighDivergence, breakEvenDay, verdict, verdictColor, verdictBg, advice,
      priceChangeA: priceChangeA.toFixed(1),
    });
  };

  // If not on Pro+, show upgrade teaser
  if (!canAccess) {
    return (
      <UpgradeTeaser
        tier="pro"
        feature="Smart Exit Timer"
        description="Know exactly when to exit your LP position. Get a HOLD / HARVEST / EXIT verdict based on your real entry prices, IL accumulation, and yield earned."
        onSignIn={() => {}}
      >
        {/* Static blurred preview */}
        <div className="space-y-4 p-1">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div className="neon-card p-4 rounded-xl">
                <h4 className="text-xs font-bold text-white mb-3">Position Details</h4>
                <div className="space-y-2">
                  <div className="h-8 bg-gray-800 rounded-lg" />
                  <div className="grid grid-cols-2 gap-2"><div className="h-8 bg-gray-800 rounded" /><div className="h-8 bg-gray-800 rounded" /></div>
                  <div className="grid grid-cols-2 gap-2"><div className="h-8 bg-gray-800 rounded" /><div className="h-8 bg-gray-800 rounded" /></div>
                </div>
              </div>
              <div className="neon-card p-4 rounded-xl">
                <h4 className="text-xs font-bold text-white mb-3">Token Prices</h4>
                <div className="grid grid-cols-2 gap-2">{[0,1,2,3].map(i=><div key={i} className="h-8 bg-gray-800 rounded" />)}</div>
              </div>
              <div className="h-11 bg-[#00d4ff]/20 rounded-xl" />
            </div>
            <div className="space-y-3">
              <div className="p-5 rounded-xl border border-[#00ff88]/40 bg-[#00ff88]/5">
                <div className="flex justify-between mb-3"><span className="h-3 w-24 bg-gray-700 rounded" /><span className="h-5 w-20 bg-[#00ff88]/20 rounded-full" /></div>
                <p className="text-3xl font-bold text-[#00ff88] mb-2" title="Calculated based on yield vs impermanent loss dynamics">Optimized Hold Time: 30 Days</p>
                <p className="text-xs text-gray-400">Yield is 2.1× your IL. Consider harvesting the +$284 gain.</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="neon-card p-3 rounded-lg"><p className="text-[10px] text-gray-500 mb-1">Yield Earned</p><p className="text-sm font-bold text-[#00ff88]">+$369.86</p></div>
                <div className="neon-card p-3 rounded-lg"><p className="text-[10px] text-gray-500 mb-1">IL Cost</p><p className="text-sm font-bold text-red-400">-$70.43</p></div>
                <div className="neon-card p-3 rounded-lg"><p className="text-[10px] text-gray-500 mb-1">Net LP P&L</p><p className="text-sm font-bold text-[#00ff88]">+$284.43</p></div>
                <div className="neon-card p-3 rounded-lg"><p className="text-[10px] text-gray-500 mb-1">ETH Change</p><p className="text-sm font-bold text-[#00ff88]">+12.5%</p></div>
              </div>
            </div>
          </div>
        </div>
      </UpgradeTeaser>
    );
  }

  return (
    <div className="space-y-4" data-testid="smart-exit-timer">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#00d4ff]" />
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">Smart Exit Timer <FeatureInfo featureKey="smart_exit_timer" size="xs" /></h3>
            <p className="text-[10px] text-gray-500">Know exactly when to exit your LP position</p>
          </div>
        </div>
        {result && (
          <button onClick={resetCalculator}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-gray-400 hover:text-white border border-gray-700 rounded-lg text-xs transition-colors"
            data-testid="exit-timer-reset">
            <RefreshCw className="w-3 h-3" /> New Calculation
          </button>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Inputs */}
        <div className="space-y-3">
          <div className="neon-card p-4 rounded-xl">
            <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2"><Target className="w-3.5 h-3.5 text-[#00d4ff]" /> Position Details</h4>
            <div className="space-y-3">
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Entry Date</label>
                <input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} max={today}
                  className="w-full px-3 py-2 bg-[#0a0a12] border border-gray-700 rounded-lg text-sm text-white focus:outline-none focus:border-[#00d4ff]/50"
                  data-testid="exit-entry-date" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Deposit ($)</label>
                  <input type="number" value={depositAmount} onChange={e => setDepositAmount(Number(e.target.value))} placeholder="5000"
                    className="w-full px-2 py-1.5 bg-[#0a0a12] border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-[#00d4ff]/50"
                    data-testid="exit-deposit" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Pool APY (%)</label>
                  <input type="number" value={poolApy} onChange={e => setPoolApy(Number(e.target.value))} placeholder="45"
                    className="w-full px-2 py-1.5 bg-[#0a0a12] border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-[#00d4ff]/50"
                    data-testid="exit-apy" />
                </div>
              </div>
              {/* Dynamic token selectors — supports 2-5 tokens for multi-token pools */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] text-gray-500">Tokens ({tokens.length})</label>
                  {tokens.length < MAX_TOKENS && (
                    <button onClick={addToken} className="text-[9px] text-[#00d4ff] hover:underline font-bold" data-testid="add-token-btn">
                      + Add Token
                    </button>
                  )}
                </div>
                <div className={`grid gap-2 ${tokens.length <= 3 ? 'grid-cols-' + tokens.length : 'grid-cols-3'}`}>
                  {tokens.map((token, i) => (
                    <div key={i} className="relative">
                      <select value={token.symbol} onChange={e => updateToken(i, 'symbol', e.target.value)}
                        className="w-full px-2 py-1.5 bg-[#0a0a12] border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-[#00d4ff]/50"
                        data-testid={`exit-token-${i}-select`}>
                        {ALL_TOKENS.map(t => <option key={t}>{t}</option>)}
                      </select>
                      {tokens.length > 2 && (
                        <button onClick={() => removeToken(i)}
                          className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500/80 hover:bg-red-500 rounded-full flex items-center justify-center text-[8px] text-white"
                          data-testid={`remove-token-${i}-btn`}>×</button>
                      )}
                    </div>
                  ))}
                </div>
                {tokens.length > 2 && <p className="text-[9px] text-gray-600 mt-1">{tokens.length}-token pool (e.g. {tokens.map(t => t.symbol).join('/')})</p>}
              </div>
            </div>
          </div>

          {/* Prices with Live Price buttons — dynamic for N tokens */}
          <div className="neon-card p-4 rounded-xl">
            <h4 className="text-xs font-bold text-white mb-3 flex items-center gap-2"><DollarSign className="w-3.5 h-3.5 text-[#ffa502]" /> Token Prices</h4>
            <div className="space-y-3">
              {tokens.map((token, i) => (
                <div key={i} className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-gray-500">{token.symbol} entry ($)</label>
                    <input type="number" value={token.entryPrice} onChange={e => updateToken(i, 'entryPrice', e.target.value)}
                      placeholder={STABLECOINS_SET.has(token.symbol.toUpperCase()) ? 'e.g. 1.00' : 'e.g. 1800'}
                      className="w-full px-2 py-1.5 mt-1 bg-[#0a0a12] border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-[#ffa502]/50"
                      data-testid={`exit-entry-${i}`} />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-[10px] text-[#ffa502]/70">{token.symbol} now ($) <span className="text-gray-700">*</span></label>
                      <button onClick={() => getLivePrice(token.symbol, i)} disabled={fetchingPrice === i}
                        className="text-[9px] text-[#00ff88] hover:underline disabled:opacity-50 font-bold" data-testid={`live-price-${i}`}>
                        {fetchingPrice === i ? '...' : '↗ live'}
                      </button>
                    </div>
                    <input type="number" value={token.currentPrice} onChange={e => updateToken(i, 'currentPrice', e.target.value)} placeholder="Today's price"
                      className="w-full px-2 py-1.5 bg-[#0a0a12] border border-[#ffa502]/30 rounded text-xs text-white focus:outline-none focus:border-[#ffa502]/60"
                      data-testid={`exit-current-${i}`} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button onClick={calculate}
            className="w-full py-3 bg-[#00d4ff]/20 text-[#00d4ff] border border-[#00d4ff]/40 rounded-xl text-sm font-bold hover:bg-[#00d4ff]/30 transition-all flex items-center justify-center gap-2"
            data-testid="calculate-exit-btn">
            <Clock className="w-4 h-4" /> Analyze My Position
          </button>
        </div>

        {/* Results */}
        <div>
          {!result ? (
            <div className="neon-card rounded-xl p-6 flex flex-col items-center justify-center h-full min-h-[300px] text-center border border-dashed border-gray-700">
              <Clock className="w-10 h-10 text-[#00d4ff]/30 mb-3" />
              <p className="text-sm text-gray-500 font-medium">Enter your position details</p>
              <p className="text-xs text-gray-600 mt-1">Get a HOLD / HARVEST / EXIT recommendation based on your real prices, IL, and yield</p>
              <p className="text-[10px] text-gray-700 mt-3">Tip: click <span className="text-[#00ff88]">↗ live</span> next to current prices to auto-fill today's market price</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="exit-results">
              {/* Verdict */}
              <div className={`p-5 rounded-xl border ${result.verdictBg}`}>
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider">Exit Recommendation</span>
                  <span className={`text-xs font-bold px-3 py-1 rounded-full border ${result.verdictBg} ${result.verdictColor}`}>{result.verdict}</span>
                </div>
                <p className={`text-3xl font-bold tabular-nums ${result.verdictColor} mb-2`}>
                  {result.daysHeld >= result.breakEvenDay
                    ? '✅ Ready to exit'
                    : result.breakEvenDay - result.daysHeld === 1
                      ? 'Hold for 1 more Day'
                      : `Hold for ${Math.max(0, result.breakEvenDay - result.daysHeld)} more Days`}
                </p>
                <p className="text-[10px] text-gray-500 mt-1" title="Calculated based on yield vs impermanent loss dynamics">
                  {result.daysHeld} {result.daysHeld === 1 ? 'Day' : 'Days'} held · Break-even at Day {result.breakEvenDay}
                </p>
                <p className="text-xs text-gray-400 leading-relaxed">{result.advice}</p>
              </div>

              {/* Metrics Grid */}
              <div className="grid grid-cols-2 gap-2">
                <div className="neon-card p-3 rounded-lg">
                  <p className="text-[10px] text-gray-500 mb-1">Yield Earned</p>
                  <p className="text-sm font-bold text-[#00ff88]">+${parseFloat(result.yieldEarned).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                </div>
                <div className="neon-card p-3 rounded-lg">
                  <p className="text-[10px] text-gray-500 mb-1">IL Cost</p>
                  <p className="text-sm font-bold text-red-400">-${parseFloat(result.ilUSD).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                </div>
                <div className="neon-card p-3 rounded-lg">
                  <p className="text-[10px] text-gray-500 mb-1">Net LP P&L</p>
                  <p className={`text-sm font-bold ${parseFloat(result.netPnL) >= 0 ? 'text-[#00ff88]' : 'text-red-400'}`}>{parseFloat(result.netPnL) >= 0 ? '+' : ''}${parseFloat(result.netPnL).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}</p>
                </div>
                <div className="neon-card p-3 rounded-lg">
                  <p className="text-[10px] text-gray-500 mb-1">{result.firstTokenSymbol || tokens[0]?.symbol} Change</p>
                  <p className={`text-sm font-bold ${parseFloat(result.priceChangeFirst) >= 0 ? 'text-[#00ff88]' : 'text-red-400'}`}>{parseFloat(result.priceChangeFirst) >= 0 ? '+' : ''}{result.priceChangeFirst}%</p>
                </div>
              </div>

              {/* Opportunity Cost — shown when LP underperforms HODLing significantly */}
              {parseFloat(result.opportunityCost) < -50 && (
                <div className="neon-card p-3 rounded-xl border border-[#ffa502]/20">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-[#ffa502] flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-bold text-[#ffa502] mb-0.5">Opportunity Cost vs HODLing</p>
                      <p className="text-[10px] text-gray-400">
                        If you had held equally outside the pool, you'd have earned{' '}
                        <span className="text-[#00ff88]">+${Math.abs(parseFloat(result.hodlPnL)).toLocaleString('en-US', {maximumFractionDigits: 0})}</span> more.
                        Your LP earned less due to IL capturing {result.ilAbs}% of your position — but you still have your tokens.
                      </p>
                      <p className="text-[10px] text-gray-600 mt-1">
                        Net LP gain: ${parseFloat(result.netPnL).toFixed(0)} · HODL gain: ${parseFloat(result.hodlPnL).toFixed(0)} · Difference: ${parseFloat(result.opportunityCost).toFixed(0)}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Break-even Progress */}
              <div className="neon-card p-4 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-400">Break-even Progress</p>
                  <p className="text-xs font-bold text-[#ffa502]">Day {result.breakEvenDay}</p>
                </div>
                <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#ffa502] to-[#00ff88] transition-all duration-500"
                    style={{ width: `${Math.min(100, (result.daysHeld / Math.max(result.breakEvenDay, 1)) * 100)}%` }} />
                </div>
                <div className="flex justify-between text-[10px] text-gray-600 mt-1">
                  <span>Entry</span>
                  <span className="text-[#ffa502]">Break-even (Day {result.breakEvenDay})</span>
                </div>
              </div>

              <div className="text-[10px] text-gray-700 text-center">IL = {result.ilPct}% · Formula: {(result.tokenCount || 2) > 2 ? `N·GM(ratios)/Σ(ratios)−1 (${result.tokenCount}-token)` : '2√k/(1+k)−1'}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
// ─── Advanced Backtesting Panel (Elite) ──────────────────
const BacktestPanel = ({ depositAmount, grossApy, selectedChain, isStablecoin, token }) => {
  const [backtest, setBacktest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(90);

  // Pool search state
  const [poolSearch, setPoolSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedPool, setSelectedPool] = useState(null);
  const backtestPoolAnchorRef = useRef(null);

  // Use selected pool's data or fall back to parent props
  const activeApy = selectedPool ? selectedPool.apy_total : grossApy;
  const activeChain = selectedPool ? selectedPool.chain : selectedChain;

  // Debounced pool search
  useEffect(() => {
    if (!poolSearch || poolSearch.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    const t = setTimeout(async () => {
      setIsSearching(true);
      try {
        const res = await fetch(`${API_URL}/api/yield-intelligence/pools?search=${encodeURIComponent(poolSearch)}&limit=8&sort_by=apy`);
        if (res.ok) {
          const data = await res.json();
          setSearchResults(data.pools || []);
          setShowResults(true);
        }
      } catch (e) { console.error(e); }
      setIsSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [poolSearch]);

  const runBacktest = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/yield-intelligence/backtest`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          deposit_amount: depositAmount,
          gross_apy: activeApy,
          chain: activeChain,
          days,
          is_stablecoin: selectedPool ? selectedPool.stable_coin : isStablecoin,
          symbol: selectedPool?.symbol || null,   // enables LP-pair-specific Bitquery data
        }),
      });
      const data = await res.json();
      if (data.blur) {
        toast.error('Advanced Backtesting requires Elite plan');
      } else {
        setBacktest(data);
      }
    } catch (e) {
      toast.error('Backtest failed. Please try again.');
    }
    setLoading(false);
  };

  useEffect(() => {
    runBacktest();
  }, [depositAmount, activeApy, activeChain, isStablecoin, days, selectedPool]);

  const scenarioColors = {
    'Bull Market':   { bg: 'bg-green-500/10',  border: 'border-green-500/30',  text: 'text-green-400',  icon: TrendingUp },
    'Stable Market': { bg: 'bg-[#00ff88]/10',  border: 'border-[#00ff88]/30', text: 'text-[#00ff88]',  icon: Minus },
    'Bear Market':   { bg: 'bg-red-500/10',    border: 'border-red-500/30',   text: 'text-red-400',    icon: TrendingDown },
    'Sideways Grind':{ bg: 'bg-[#ffa502]/10',  border: 'border-[#ffa502]/30', text: 'text-[#ffa502]',  icon: Minus },
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-5 h-5 text-amber-400" />
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-2">Advanced Backtesting <FeatureInfo featureKey="advanced_backtesting" size="xs" /></h3>
            <p className="text-[10px] text-amber-400 flex items-center gap-1">
              <Cpu className="w-3 h-3" /> Powered by Nighton Intelligence Engine
            </p>
          </div>
        </div>
        <button
          onClick={runBacktest}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 text-amber-400 rounded-lg text-xs font-medium hover:bg-amber-500/20 transition-colors disabled:opacity-50"
          data-testid="run-backtest-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Rerun
        </button>
      </div>

      {/* Time Period */}
      <div className="neon-card p-4 rounded-xl">
        <label className="block text-xs text-gray-400 mb-2">Backtest Period</label>
        <input
          type="range" min="30" max="365" step="30"
          value={days}
          onChange={e => setDays(Number(e.target.value))}
          className="w-full accent-[#ffa502]"
        />
        <div className="flex justify-between text-xs mt-1">
          <span className="text-gray-500">30d</span>
          <span className="text-amber-400 font-bold">{days} days</span>
          <span className="text-gray-500">365d</span>
        </div>
      </div>

      {/* Pool Search */}
      <div className="neon-card p-4 rounded-xl overflow-visible">
        <label className="block text-xs text-gray-400 mb-2">Backtest a Specific Pool (Optional)</label>
        {selectedPool ? (
          <div className="flex items-center gap-3 p-3 bg-[#0a0a12] rounded-lg border border-amber-500/30">
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white truncate">{selectedPool.symbol}</p>
              <p className="text-xs text-gray-500">{selectedPool.protocol} · {selectedPool.chain} · <span className="text-[#00ff88]">{selectedPool.apy_total?.toFixed(2)}% APY</span></p>
            </div>
            <button
              onClick={() => setSelectedPool(null)}
              className="text-gray-500 hover:text-white flex-shrink-0 p-1"
              data-testid="backtest-clear-pool-btn"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="relative overflow-visible" ref={backtestPoolAnchorRef}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none z-10" />
            <input
              type="text"
              value={poolSearch}
              onChange={e => setPoolSearch(e.target.value)}
              placeholder="Search pool by token, protocol, chain..."
              className="w-full pl-10 pr-9 py-2.5 bg-[#0a0a12] border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20"
              data-testid="backtest-pool-search"
            />
            {isSearching
              ? <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 animate-spin" />
              : poolSearch && (
                <button onClick={() => { setPoolSearch(''); setShowResults(false); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                  <X className="w-4 h-4" />
                </button>
              )
            }
            <PoolSearchDropdownPortal open={Boolean(showResults && searchResults.length)} anchorRef={backtestPoolAnchorRef}>
              {searchResults.map(p => (
                <button
                  key={p.pool_id}
                  type="button"
                  onClick={() => { setSelectedPool(p); setPoolSearch(''); setShowResults(false); toast.success(`Loaded ${p.symbol} for backtesting`); }}
                  className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-800/60 border-b border-gray-800/50 last:border-0 text-left"
                  data-testid={`backtest-pool-option-${p.pool_id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-white truncate">{p.symbol}</p>
                    <p className="text-xs text-gray-500">{p.protocol} · {p.chain}</p>
                  </div>
                  <span className="text-sm font-bold text-[#00ff88] ml-3 flex-shrink-0">{p.apy_total?.toFixed(1)}%</span>
                </button>
              ))}
            </PoolSearchDropdownPortal>
          </div>
        )}
        <p className="text-[10px] text-gray-600 mt-2">
          {selectedPool
            ? `Using ${selectedPool.symbol} data (${activeApy.toFixed(1)}% APY on ${activeChain})`
            : `Using your simulator settings — $${depositAmount.toLocaleString()} at ${activeApy.toFixed(1)}% APY on ${activeChain.charAt(0).toUpperCase() + activeChain.slice(1)}. Search a specific pool above for LP-pair historical data.`}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <Brain className="w-8 h-8 text-amber-400 animate-pulse mx-auto mb-2" />
            <p className="text-xs text-gray-500">Nighton computing scenarios...</p>
          </div>
        </div>
      ) : backtest ? (
        <>
          {/* Context Banner — shown when no specific pool is selected */}
          {!selectedPool && (
            <div className="flex items-start gap-3 px-3 py-2.5 bg-amber-500/5 border border-amber-500/20 rounded-lg" data-testid="backtest-context-banner">
              <Info className="w-3.5 h-3.5 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-amber-300/70 leading-relaxed">
                Projections for <span className="text-white font-medium">${depositAmount.toLocaleString()} deposit</span> at{' '}
                <span className="text-white font-medium">{activeApy.toFixed(1)}% APY</span> on{' '}
                <span className="text-white font-medium">{activeChain.charAt(0).toUpperCase() + activeChain.slice(1)}</span>{' '}
                using Monte Carlo modelling. Search a specific pool above to run real LP-pair historical data via Bitquery.
              </p>
            </div>
          )}
          {/* Scenario Cards */}
          <div className="grid grid-cols-2 gap-3">
            {backtest.scenarios?.map((s, i) => {
              const style = scenarioColors[s.scenario] || { bg: 'bg-gray-900/50', border: 'border-gray-800', text: 'text-gray-400', icon: Minus };
              const ScIcon = style.icon;
              return (
                <div key={i} className={`${style.bg} border ${style.border} rounded-xl p-4`} data-testid={`backtest-scenario-${i}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <ScIcon className={`w-4 h-4 ${style.text}`} />
                    <span className={`text-xs font-bold ${style.text}`}>{s.scenario}</span>
                  </div>
                  <p className="text-[10px] text-gray-500 mb-3">{s.description}</p>
                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Net APY</span>
                      <span className={`font-bold ${s.net_apy > 0 ? style.text : 'text-red-400'}`}>{s.net_apy?.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Projected Profit</span>
                      <span className={`font-bold ${s.projected_profit >= 0 ? 'text-[#00ff88]' : 'text-red-400'}`}>
                        {s.projected_profit >= 0 ? '+' : ''}${s.projected_profit?.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Alpha vs HODL</span>
                      <span className={`font-bold ${s.alpha_vs_hodl >= 0 ? 'text-[#00d4ff]' : 'text-red-400'}`}>
                        {s.alpha_vs_hodl >= 0 ? '+' : ''}${s.alpha_vs_hodl?.toFixed(0)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Break-even</span>
                      <span className="text-[#ffa502] font-bold">{s.break_even_days?.toFixed(0)} {Number(s.break_even_days?.toFixed(0)) === 1 ? 'Day' : 'Days'}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Summary */}
          <div className="neon-card p-4 rounded-xl">
            <h4 className="text-xs font-bold text-white mb-3">Scenario Summary</h4>
            <div className="space-y-2">
              {backtest.scenarios?.map((s, i) => {
                const allVals = backtest.scenarios.map(x => x.net_apy);
                const maxVal = Math.max(...allVals);
                const minVal = Math.min(0, Math.min(...allVals));
                const range = maxVal - minVal || 1;
                const barPct = Math.max(0, Math.min(100, ((s.net_apy - minVal) / range) * 100));
                const barColor = s.net_apy > 30 ? '#00ff88' : s.net_apy > 0 ? '#ffa502' : '#f87171';
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-gray-500 w-28 flex-shrink-0">{s.scenario}</span>
                    <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${barPct}%`, backgroundColor: barColor }} />
                    </div>
                    <span className={`text-xs font-bold w-14 text-right ${s.net_apy > 0 ? 'text-[#00ff88]' : 'text-red-400'}`}>
                      {s.net_apy?.toFixed(1)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          <p className="text-[10px] text-center text-amber-400/60">{backtest.note}</p>
              <div className="flex items-center justify-center gap-2 mt-1">
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${backtest.data_source === 'bitquery_lp_pair' ? 'text-[#00ff88] border-[#00ff88]/30 bg-[#00ff88]/5' : backtest.data_source === 'bitquery_proxy' ? 'text-[#00d4ff] border-[#00d4ff]/30 bg-[#00d4ff]/5' : 'text-amber-400/60 border-amber-400/20 bg-amber-400/5'}`}>
                  {backtest.data_source === 'bitquery_lp_pair' ? `Real ${backtest.parameters?.symbol || 'LP'} data` : backtest.data_source === 'bitquery_proxy' ? 'ETH/USDC proxy data' : 'Monte Carlo projection'}
                </span>
              </div>
        </>
      ) : null}
    </div>
  );
};

// ─── Main Simulator Labs Component ───────────────────────────
export default function SimulatorLabs() {
  const { user, token, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('simulator');

  // Check for pre-filled pool data from URL params
  const poolFromUrl = useMemo(() => {
    const symbol = searchParams.get('symbol');
    const protocol = searchParams.get('protocol');
    const chain = searchParams.get('chain');
    const apy = searchParams.get('apy');
    const stablecoin = searchParams.get('stablecoin');
    
    if (symbol && apy) {
      return {
        symbol,
        protocol,
        chain: chain?.toLowerCase() || 'arbitrum',
        apy: parseFloat(apy) || 50,
        stablecoin: stablecoin === '1',
      };
    }
    return null;
  }, [searchParams]);

  // Read simulator-specific share params from URL (deposit, days, divergence, tvl)
  const simFromUrl = useMemo(() => {
    const deposit  = searchParams.get('deposit');
    const days     = searchParams.get('days');
    const diverge  = searchParams.get('divergence');
    const tvl      = searchParams.get('tvl');
    const apy      = searchParams.get('apy');
    const chain    = searchParams.get('chain');
    const stable   = searchParams.get('stablecoin');
    // Only activate when direct sim share link (no pool symbol)
    if (!searchParams.get('symbol') && (deposit || days || apy)) {
      return {
        deposit:    deposit  ? Math.min(100000, Math.max(100, parseInt(deposit)  || 1000)) : null,
        days:       days     ? Math.min(365,   Math.max(7,   parseInt(days)     || 60))   : null,
        apy:        apy      ? parseFloat(apy) || 50 : null,
        divergence: diverge  ? parseFloat(diverge) || 0 : null,
        tvl:        tvl      || null,
        chain:      chain?.toLowerCase() || null,
        stablecoin: stable   ? stable === '1' : null,
      };
    }
    return null;
  }, [searchParams]);

  // Simulation inputs - use URL params if available
  const [depositAmount, setDepositAmount] = useState(simFromUrl?.deposit ?? 1000);
  const [holdingDays, setHoldingDays] = useState(simFromUrl?.days ?? 60);
  // String states for text inputs — allows clearing/editing without snapping back
  const [depositStr, setDepositStr] = useState(String(simFromUrl?.deposit ?? 1000));
  const [holdingStr, setHoldingStr] = useState(String(simFromUrl?.days ?? 60));
  const [selectedChain, setSelectedChain] = useState(simFromUrl?.chain || poolFromUrl?.chain || 'arbitrum');
  const [grossApy, setGrossApy] = useState(simFromUrl?.apy ?? poolFromUrl?.apy ?? 50);
  const [apyStr, setApyStr] = useState(String(simFromUrl?.apy ?? poolFromUrl?.apy ?? 50));
  const [priceDivergence, setPriceDivergence] = useState(simFromUrl?.divergence ?? 0);
  const [isStablecoin, setIsStablecoin] = useState(simFromUrl?.stablecoin ?? poolFromUrl?.stablecoin ?? false);

  // Pool link for simulator context
  const [simPoolSearch, setSimPoolSearch] = useState('');
  const [simPoolResults, setSimPoolResults] = useState([]);
  const [simPoolSearching, setSimPoolSearching] = useState(false);
  const [simLinkedPool, setSimLinkedPool] = useState(null);
  const [showSimPoolResults, setShowSimPoolResults] = useState(false);

  // Debounced search for simulator pool link
  useEffect(() => {
    if (!simPoolSearch || simPoolSearch.length < 2) {
      setSimPoolResults([]);
      setShowSimPoolResults(false);
      return;
    }
    const t = setTimeout(async () => {
      setSimPoolSearching(true);
      try {
        const res = await fetch(`${API_URL}/api/yield-intelligence/pools?search=${encodeURIComponent(simPoolSearch)}&limit=6&sort_by=apy`);
        if (res.ok) {
          const data = await res.json();
          setSimPoolResults(data.pools || []);
          setShowSimPoolResults(true);
        }
      } catch (e) { console.error(e); }
      setSimPoolSearching(false);
    }, 300);
    return () => clearTimeout(t);
  }, [simPoolSearch]);

  // Update state when URL params change
  useEffect(() => {
    if (poolFromUrl) {
      setSelectedChain(poolFromUrl.chain);
      setGrossApy(poolFromUrl.apy); setApyStr(String(poolFromUrl.apy));

      // Show toast that pool was loaded
      toast.success(`Loaded ${poolFromUrl.symbol} from ${poolFromUrl.protocol}`);
    }
  }, [poolFromUrl?.symbol]);

  // Simulation results
  const [simulation, setSimulation] = useState(null);
  const [chartData, setChartData] = useState([]);

  // Enhanced cost inputs
  const [tvlTier, setTvlTier] = useState(simFromUrl?.tvl || 'medium');
  const [gasEntry, setGasEntry] = useState(null);
  const [gasExit, setGasExit] = useState(null);
  const [autoFillLoading, setAutoFillLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  /** Live = RPC gas fetch succeeded for this chain; Estimated = fallback defaults */
  const [gasPriceLabel, setGasPriceLabel] = useState('Estimated');
  const simPoolAnchorRef = useRef(null);

  const userTier = user?.subscription?.tier || 'free';
  const isAdmin = user?.role === 'admin';
  const canAccessSimulator = isAuthenticated && (userTier !== 'free' || isAdmin);
  const isPremium = userTier === 'pro' || userTier === 'elite' || userTier === 'unlimited' || isAdmin;
  const isElite = userTier === 'elite' || userTier === 'unlimited' || isAdmin;

  const chains = ['ethereum', 'arbitrum', 'polygon', 'optimism', 'base', 'bsc', 'avalanche', 'fantom', 'zksync', 'linea'];

  // Chain gas fee defaults — 10 chains
  const CHAIN_GAS_DEFAULTS = {
    ethereum:  { entry: 8.00, exit: 8.00 },
    arbitrum:  { entry: 0.10, exit: 0.10 },
    base:      { entry: 0.05, exit: 0.05 },
    optimism:  { entry: 0.08, exit: 0.08 },
    polygon:   { entry: 0.02, exit: 0.02 },
    bsc:       { entry: 0.20, exit: 0.20 },
    avalanche: { entry: 0.30, exit: 0.30 },
    fantom:    { entry: 0.01, exit: 0.01 },
    zksync:    { entry: 0.08, exit: 0.08 },
    linea:     { entry: 0.06, exit: 0.06 },
  };

  const TVL_SLIPPAGE = {
    high:   { pct: 0.10, label: 'High TVL (>$10M)' },
    medium: { pct: 0.25, label: 'Med TVL ($1M–$10M)' },
    low:    { pct: 0.50, label: 'Low TVL (<$1M)' },
  };

  // Scenario Presets
  const SCENARIO_PRESETS = [
    {
      id: 'stable_pair',
      label: 'Stable Pair',
      emoji: '🟢',
      description: 'USDC/USDT — near-zero risk',
      deposit: 1000, apy: 8, chain: 'base', tvlTier: 'high', stablecoin: true, priceDivergence: 0, holdingDays: 30,
    },
    {
      id: 'blue_chip',
      label: 'Blue Chip LP',
      emoji: '🔵',
      description: 'ETH/USDC — balanced risk',
      deposit: 5000, apy: 25, chain: 'arbitrum', tvlTier: 'high', stablecoin: false, priceDivergence: 20, holdingDays: 60,
    },
    {
      id: 'mid_range',
      label: 'Mid-Range',
      emoji: '🟡',
      description: 'ETH/WBTC — moderate yield',
      deposit: 2000, apy: 65, chain: 'arbitrum', tvlTier: 'medium', stablecoin: false, priceDivergence: 30, holdingDays: 45,
    },
    {
      id: 'high_apy',
      label: 'High APY',
      emoji: '🟠',
      description: 'Alt pairs — active management',
      deposit: 1000, apy: 120, chain: 'base', tvlTier: 'medium', stablecoin: false, priceDivergence: 50, holdingDays: 30,
    },
    {
      id: 'degen',
      label: 'Degen Mode',
      emoji: '🔴',
      description: 'New pairs — high risk/reward',
      deposit: 500, apy: 200, chain: 'arbitrum', tvlTier: 'low', stablecoin: false, priceDivergence: 80, holdingDays: 14,
    },
  ];

  const [activePreset, setActivePreset] = useState(null);

  const applyPreset = (preset) => {
    setDepositAmount(preset.deposit);
    setDepositStr(String(preset.deposit));
    setGrossApy(preset.apy); setApyStr(String(preset.apy));
    handleChainChange(preset.chain);
    setTvlTier(preset.tvlTier);
    setIsStablecoin(preset.stablecoin);
    setPriceDivergence(preset.priceDivergence);
    setHoldingDays(preset.holdingDays);
    setHoldingStr(String(preset.holdingDays));
    setActivePreset(preset.id);
    toast.success(`Loaded "${preset.label}" scenario`);
  };

  // Reset simulator to clean defaults
  const resetAll = () => {
    setDepositAmount(1000);
    setDepositStr('1000');
    setHoldingDays(60);
    setHoldingStr('60');
    setGrossApy(50); setApyStr('50');
    handleChainChange('arbitrum');
    setIsStablecoin(false);
    setPriceDivergence(0);
    setTvlTier('medium');
    setActivePreset(null);
    toast.success('Simulator reset to defaults');
  };

  // Share current simulation as a deep link
  const shareSimulation = async () => {
    const params = new URLSearchParams({
      deposit:    depositAmount,
      days:       holdingDays,
      apy:        grossApy,
      chain:      selectedChain,
      stablecoin: isStablecoin ? '1' : '0',
      divergence: priceDivergence,
      tvl:        tvlTier,
    });
    const url = `${window.location.origin}/simulator?${params.toString()}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Simulation link copied!');
    } catch {
      toast.info(`Share link: ${url}`);
    }
  };

  // Global keyboard shortcuts — only when simulator tab is active and not focused in an input
  useEffect(() => {    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (activeTab !== 'simulator') return;
      if (e.key === 'r' || e.key === 'R') { e.preventDefault(); resetAll(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeGasEntry = gasEntry ?? CHAIN_GAS_DEFAULTS[selectedChain]?.entry ?? 0.10;
  const activeGasExit = gasExit ?? CHAIN_GAS_DEFAULTS[selectedChain]?.exit ?? 0.10;
  const activeSlippage = ((TVL_SLIPPAGE[tvlTier]?.pct || 0.25) / 100) * depositAmount;

  // Auto-fill market rates from DeFiLlama
  const autoFillMarketRates = async () => {
    setAutoFillLoading(true);
    try {
      if (simLinkedPool) {
        const apy = Math.round(simLinkedPool.apy_total || 50);
        setGrossApy(apy); setApyStr(String(apy));
        toast.success(`Market rate for ${simLinkedPool.symbol}: ${apy}% APY`);
      } else {
        const res = await fetch(`${API_URL}/api/yield-intelligence/pools?chains=${selectedChain}&sort_by=apy&limit=3&min_tvl=1000000`);
        if (res.ok) {
          const data = await res.json();
          const pools = data.pools || [];
          if (pools.length > 0) {
            const topPool = pools[0];
            const apy = Math.round(topPool.apy_total || 50);
            setGrossApy(apy); setApyStr(String(apy));
            toast.success(`Top ${selectedChain} rate: ${apy}% APY (${topPool.symbol})`);
          }
        }
      }
    } catch {
      toast.error('Could not fetch market rates — enter APY manually');
    }
    setAutoFillLoading(false);
  };

  // Reset gas when chain changes
  const handleChainChange = (chain) => {
    setSelectedChain(chain);
    setGasEntry(null);
    setGasExit(null);
    setGasPriceLabel('Estimated');
  };

  // Live gas from backend (RPC where available) — fills entry/exit when chain changes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/yield-intelligence/gas-costs`);
        if (!res.ok || cancelled) return;
        const data = await res.json();
        const row = data.gas_costs?.[selectedChain];
        if (!row || cancelled) return;
        setGasEntry(Number(row.deposit_usd));
        setGasExit(Number(row.withdraw_usd));
        setGasPriceLabel(row.is_live ? 'Live' : 'Estimated');
      } catch {
        if (!cancelled) setGasPriceLabel('Estimated');
      }
    })();
    return () => { cancelled = true; };
  }, [selectedChain]);

  // Calculate IL from price divergence
  const calculateIL = (divergence) => {
    if (divergence === 0) return 0;
    const d = 1 + (divergence / 100);
    const il = (2 * Math.sqrt(d)) / (1 + d) - 1;
    return Math.abs(il * 100);
  };

  const currentIL = useMemo(() => calculateIL(priceDivergence), [priceDivergence]);

  // Computed cost values (depend on currentIL, so must be after it)
  const activeILCost = isStablecoin ? 0 : (currentIL / 100) * depositAmount;
  const totalEnhancedCosts = activeGasEntry + activeGasExit + activeSlippage + activeILCost;

  // Frontend-computed stats — fully consistent with the displayed cost breakdown
  const dailyYield = (grossApy / 100 / 365) * depositAmount;
  const totalYield60d = dailyYield * holdingDays;
  const frontendNetAPY = depositAmount > 0
    ? Math.max(-100, grossApy - (totalEnhancedCosts / depositAmount) * (365 / Math.max(holdingDays, 1)) * 100)
    : 0;
  const frontendBreakEvenDays = dailyYield > 0 ? totalEnhancedCosts / dailyYield : 0;
  const frontendNetProfit = totalYield60d - totalEnhancedCosts;
  const frontendRatingScore = frontendNetAPY >= 100 ? 'A' : frontendNetAPY >= 50 ? 'B' : frontendNetAPY >= 20 ? 'C' : frontendNetAPY > 0 ? 'D' : 'F';
  const frontendRatingLabel = frontendNetAPY >= 100 ? 'Exceptional' : frontendNetAPY >= 50 ? 'Strong' : frontendNetAPY >= 20 ? 'Moderate' : frontendNetAPY > 0 ? 'Weak' : 'Loss';

  // Run simulation
  const runSimulation = async () => {
    setLoading(true);
    try {
      const ilRisk = currentIL > 10 ? 'high' : currentIL > 3 ? 'medium' : 'low';
      
      const res = await fetch(`${API_URL}/api/yield-intelligence/simulate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` })
        },
        body: JSON.stringify({
          deposit_amount: depositAmount,
          gross_apy: grossApy,
          chain: selectedChain,
          tvl_usd: 10000000,
          is_stablecoin: isStablecoin,
          il_risk_level: ilRisk,
          holding_days: holdingDays,
          price_divergence: priceDivergence
        })
      });

      if (res.ok) {
        const data = await res.json();
        setSimulation(data.simulation);
        
        // Generate chart data using consistent frontend cost calculation
        const days = Math.min(holdingDays, 90);
        const yieldPerDay = (grossApy / 365 / 100) * depositAmount;  // same as dailyYield
        const ilCostTotal = isStablecoin ? 0 : (currentIL / 100) * depositAmount;
        const gasCosts = activeGasEntry + activeGasExit;
        const slippageCost = ((TVL_SLIPPAGE[tvlTier]?.pct || 0.25) / 100) * depositAmount;
        const totalCosts = gasCosts + slippageCost + ilCostTotal;
        
        const newChartData = Array.from({ length: days }, (_, i) => {
          const day = i + 1;
          const grossYield = yieldPerDay * day;
          const netYield = grossYield - ilCostTotal;
          
          return {
            day,
            cumulative_yield: Math.max(0, netYield),
            total_costs: totalCosts,
            break_even_reached: netYield >= totalCosts
          };
        });
        
        setChartData(newChartData);
        setLastUpdated(Date.now());
      }
    } catch (e) {
      console.error('Simulation error:', e);
      toast.error('Simulation failed. Please try again.');
    }
    setLoading(false);
  };

  // Auto-run simulation when inputs change
  useEffect(() => {
    const timer = setTimeout(() => runSimulation(), 500);
    return () => clearTimeout(timer);
  }, [depositAmount, grossApy, selectedChain, isStablecoin, holdingDays, priceDivergence, currentIL, token, tvlTier, activeGasEntry, activeGasExit]);

  // Calculate HODL value (just holding without providing liquidity)
  // Handles both gains and losses from price divergence
  const hodlValue = useMemo(() => {
    if (priceDivergence === 0) return 0;
    
    // For a 50/50 pool, if one asset moves by X%, the HODL value is:
    // (asset1 * (1 + X%)) + asset2 = deposit * (1 + X%/2)
    // So the gain/loss vs initial deposit is:
    const hodlReturn = (priceDivergence / 2 / 100) * depositAmount;
    return hodlReturn; // Can be positive or negative
  }, [depositAmount, priceDivergence]);

  const sim = simulation;

  return (
    <div className="min-h-screen bg-[#0a0a12] p-4 md:p-6" data-testid="simulator-labs">
      {/* Header */}
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#00ff88]/10 rounded-lg">
              <FlaskConical className="w-6 h-6 text-[#00ff88]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Simulator Labs</h1>
              <p className="text-sm text-gray-400">The "What-If" engine for DeFi yields</p>
            </div>
          </div>
        </div>

        {/* Pool Pre-filled Banner */}
        {poolFromUrl && (
          <div className="bg-[#00d4ff]/10 border border-[#00d4ff]/30 rounded-xl p-4 mb-6 flex items-center gap-3" data-testid="pool-prefilled-banner">
            <Sparkles className="w-5 h-5 text-[#00d4ff] flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-white font-medium">
                Simulating: <span className="text-[#00d4ff]">{poolFromUrl.symbol}</span>
              </p>
              <p className="text-xs text-gray-400">
                {poolFromUrl.protocol} on {poolFromUrl.chain.charAt(0).toUpperCase() + poolFromUrl.chain.slice(1)} • {poolFromUrl.apy}% APY
                {poolFromUrl.stablecoin && ' • Stablecoin Pool'}
              </p>
            </div>
            <a href="/explore" className="text-xs text-[#00d4ff] hover:underline">← Back to pools</a>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex items-center gap-2 mb-6" data-testid="simulator-tabs">
          <div
            role="button"
            tabIndex={0}
            onClick={() => setActiveTab('simulator')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab('simulator'); }}}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer select-none ${
              activeTab === 'simulator'
                ? 'bg-[#00ff88]/15 text-[#00ff88] border border-[#00ff88]/30'
                : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
            }`}
            data-testid="tab-simulator"
          >
            <BarChart3 className="w-4 h-4" /> Simulator
            <FeatureInfo featureKey="simulator_overview" />
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setActiveTab('exit-timer')}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab('exit-timer'); }}}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer select-none ${
              activeTab === 'exit-timer'
                ? 'bg-[#00d4ff]/15 text-[#00d4ff] border border-[#00d4ff]/30'
                : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
            }`}
            data-testid="tab-exit-timer"
          >
            <Clock className="w-4 h-4" /> Smart Exit Timer
            <FeatureInfo featureKey="smart_exit_timer" />
          </div>
          <div
            role="button"
            tabIndex={0}
            onClick={() => setActiveTab('backtest')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveTab('backtest');
              }
            }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer select-none ${
              activeTab === 'backtest'
                ? 'bg-amber-500/15 text-amber-400 border border-amber-500/30'
                : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
            }`}
            data-testid="tab-backtest"
          >
            <Brain className="w-4 h-4" /> Advanced Backtesting
            {!isElite && <span className="text-[10px] px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded font-bold">ELITE</span>}
            <FeatureInfo featureKey="advanced_backtesting" />
          </div>
        </div>

        {/* Smart Exit Timer Tab */}
        {activeTab === 'exit-timer' && <SmartExitTimer user={user} isAuthenticated={isAuthenticated} />}

        {/* Advanced Backtesting Tab */}
        {activeTab === 'backtest' && (
          isElite ? (
            <BacktestPanel
              depositAmount={depositAmount}
              grossApy={grossApy}
              selectedChain={selectedChain}
              isStablecoin={isStablecoin}
              token={token}
            />
          ) : (
            <UpgradeTeaser
              tier="elite"
              feature="Advanced Backtesting"
              description="Replay your LP strategy against real historical price data. See exactly how it would have performed — profits, IL events, optimal entry/exit points — before risking real capital."
              blurAmount="sm"
              className="min-h-[480px]"
            >
              {/* Blurred preview of backtesting UI */}
              <div className="space-y-4 pointer-events-none">
                {/* Date range row */}
                <div className="flex gap-3">
                  <div className="flex-1 bg-gray-900/60 border border-gray-700/40 rounded-xl p-4">
                    <div className="h-3 w-24 bg-gray-700/60 rounded mb-3" />
                    <div className="h-8 bg-gray-800/60 rounded-lg" />
                  </div>
                  <div className="flex-1 bg-gray-900/60 border border-gray-700/40 rounded-xl p-4">
                    <div className="h-3 w-24 bg-gray-700/60 rounded mb-3" />
                    <div className="h-8 bg-gray-800/60 rounded-lg" />
                  </div>
                  <div className="flex-1 bg-gray-900/60 border border-gray-700/40 rounded-xl p-4">
                    <div className="h-3 w-24 bg-gray-700/60 rounded mb-3" />
                    <div className="h-8 bg-gray-800/60 rounded-lg" />
                  </div>
                </div>
                {/* Chart placeholder */}
                <div className="bg-gray-900/60 border border-gray-700/40 rounded-xl p-4 h-48">
                  <div className="h-3 w-32 bg-gray-700/60 rounded mb-4" />
                  <div className="flex items-end gap-1.5 h-28">
                    {[55,70,45,80,65,90,75,60,85,50,95,72].map((h, i) => (
                      <div key={i} className="flex-1 rounded-sm bg-[#ffa502]/20" style={{height: `${h}%`}} />
                    ))}
                  </div>
                </div>
                {/* Stats row */}
                <div className="grid grid-cols-3 gap-3">
                  {['Total Return', 'Max Drawdown', 'Win Rate'].map(label => (
                    <div key={label} className="bg-gray-900/60 border border-gray-700/40 rounded-xl p-4">
                      <div className="h-2.5 w-20 bg-gray-700/60 rounded mb-2" />
                      <div className="h-6 w-16 bg-[#ffa502]/20 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            </UpgradeTeaser>
          )
        )}

        {/* Main Simulator Tab */}
        {activeTab === 'simulator' && (
          !canAccessSimulator ? (
            <UpgradeTeaser
              tier="pro"
              feature="Full Simulator Labs"
              description="Run detailed LP simulations with custom APY, L2 gas fees, slippage analysis, and Time-to-Profit charts. Upgrade to Pro for $29/mo."
              blurAmount="sm"
            >
              {/* Blurred preview */}
              <div className="grid md:grid-cols-2 gap-6 pointer-events-none">
                <div className="space-y-4">
                  <div className="neon-card p-4 rounded-xl h-64 bg-gray-900/50" />
                  <div className="neon-card p-4 rounded-xl h-48 bg-gray-900/50" />
                </div>
                <div className="space-y-4">
                  <div className="neon-card p-4 rounded-xl h-32 bg-gray-900/50" />
                  <div className="neon-card p-4 rounded-xl h-80 bg-gray-900/50" />
                </div>
              </div>
            </UpgradeTeaser>
          ) : (
          <>
        {/* Scenario Presets Bar */}
        <div className="mb-5">
          <div className="flex items-center gap-2 mb-2.5">
            <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">Scenario Presets</span>
            <FeatureInfo featureKey="scenario_presets" size="xs" />
          </div>
          <div className="grid grid-cols-5 gap-1.5" data-testid="scenario-presets">
            {SCENARIO_PRESETS.map(preset => (
              <button
                key={preset.id}
                onClick={() => applyPreset(preset)}
                className={`px-2 py-2 rounded-xl text-left border transition-all ${
                  activePreset === preset.id
                    ? 'bg-[#00ff88]/15 border-[#00ff88]/40 text-white'
                    : 'bg-gray-800/40 border-gray-700/50 text-gray-400 hover:border-gray-600 hover:text-gray-300'
                }`}
                title={preset.description}
                data-testid={`preset-${preset.id}`}
              >
                <div className="text-base mb-0.5">{preset.emoji}</div>
                <div className="text-[10px] font-bold leading-tight">{preset.label}</div>
                <div className="text-[9px] text-gray-600 mt-0.5 leading-tight">{preset.apy}% APY</div>
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Left: Input Controls */}
          <div className="space-y-4">

            {/* Pool Link (Optional) */}
            <div className="neon-card p-4 rounded-xl overflow-visible">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-2">
                <Search className="w-3.5 h-3.5" />
                Auto-fill from a Real Pool
                <span className="normal-case text-[10px] text-gray-600 font-normal bg-gray-800 px-1.5 py-0.5 rounded">optional</span>
              </h3>
              <p className="text-[10px] text-gray-600 mb-3">
                Search any pool to auto-fill APY, chain, and stablecoin flag — or enter custom values below.
              </p>
              {simLinkedPool ? (
                <div className="flex items-center gap-3 p-2.5 bg-[#0a0a12] rounded-lg border border-[#00ff88]/25" data-testid="sim-linked-pool">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{simLinkedPool.symbol}</p>
                    <p className="text-xs text-gray-500">{simLinkedPool.protocol} · {simLinkedPool.chain} · <span className="text-[#00ff88]">{simLinkedPool.apy_total?.toFixed(2)}% APY</span></p>
                  </div>
                  <button
                    onClick={() => { setSimLinkedPool(null); }}
                    className="text-gray-500 hover:text-white p-1 flex-shrink-0"
                    data-testid="sim-clear-pool-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 pointer-events-none" />
                  <input
                    type="text"
                    value={simPoolSearch}
                    onChange={e => setSimPoolSearch(e.target.value)}
                    placeholder="e.g. USDC/USDT, ETH-USDC, Aerodrome..."
                    className="w-full pl-9 pr-8 py-2.5 bg-[#0a0a12] border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#00ff88]/40 focus:ring-1 focus:ring-[#00ff88]/10"
                    data-testid="sim-pool-search"
                  />
                  {simPoolSearching
                    ? <RefreshCw className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500 animate-spin" />
                    : simPoolSearch && (
                      <button onClick={() => { setSimPoolSearch(''); setShowSimPoolResults(false); }}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )
                  }
                  {showSimPoolResults && simPoolResults.length > 0 && (
                    <div className="absolute top-full left-0 right-0 z-[9998] mt-1 bg-[#1a1a2e] border border-gray-700 rounded-lg shadow-2xl max-h-44 overflow-y-auto">
                      {simPoolResults.map(p => (
                        <button
                          key={p.pool_id}
                          onClick={() => {
                            setSimLinkedPool(p);
                            setGrossApy(Math.round(p.apy_total || 50)); setApyStr(String(Math.round(p.apy_total || 50)));
                            setSelectedChain(p.chain || 'arbitrum');
                            setIsStablecoin(p.stable_coin || false);
                            setSimPoolSearch('');
                            setShowSimPoolResults(false);
                            toast.success(`Loaded ${p.symbol} — APY set to ${p.apy_total?.toFixed(1)}%`);
                          }}
                          className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-800/60 border-b border-gray-800/50 last:border-0 text-left"
                          data-testid={`sim-pool-option-${p.pool_id}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-white truncate">{p.symbol}</p>
                            <p className="text-xs text-gray-500">{p.protocol} · {p.chain}</p>
                          </div>
                          <span className="text-sm font-bold text-[#00ff88] ml-3 flex-shrink-0">{p.apy_total?.toFixed(1)}%</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="neon-card p-4 rounded-xl">
              <h3 className="text-sm font-bold text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-[#00ff88]" />
                Investment Parameters
                <FeatureInfo featureKey="simulator_overview" size="xs" />
              </h3>

              {/* Deposit Amount */}
              <div className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <label className="text-xs text-gray-400">Deposit Amount</label>
                  <FeatureInfo featureKey="deposit_amount" size="xs" />
                </div>
                <input 
                  type="range" min="100" max="100000" step="100"
                  value={depositAmount}
                  onChange={(e) => { setDepositAmount(Number(e.target.value)); setDepositStr(e.target.value); }}
                  className="w-full accent-[#00ff88]"
                  data-testid="deposit-slider"
                />
                <div className="flex justify-between items-center text-xs mt-1.5">
                  <span className="text-gray-500">$100</span>
                  <div className="relative">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 pointer-events-none">$</span>
                    <input
                      type="number"
                      value={depositStr}
                      min="100"
                      max="100000"
                      step="100"
                      onChange={(e) => {
                        setDepositStr(e.target.value);
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v >= 0) setDepositAmount(Math.min(100000, v));
                      }}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        const safe = (!isNaN(v) && v >= 100) ? Math.min(100000, v) : 1000;
                        setDepositAmount(safe);
                        setDepositStr(String(safe));
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') runSimulation(); }}
                      className="w-28 pl-5 pr-2 py-1 bg-[#0a0a12] border border-[#00ff88]/30 rounded-lg text-xs text-[#00ff88] font-bold text-center focus:outline-none focus:border-[#00ff88]/60"
                      data-testid="deposit-input"
                    />
                  </div>
                  <span className="text-gray-500">$100K</span>
                </div>
              </div>

              {/* Holding Days */}
              <div className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <label className="text-xs text-gray-400">Holding Period</label>
                  <FeatureInfo featureKey="holding_period" size="xs" />
                </div>
                <input 
                  type="range" min="7" max="365"
                  value={holdingDays}
                  onChange={(e) => { setHoldingDays(Number(e.target.value)); setHoldingStr(e.target.value); }}
                  className="w-full accent-[#00ff88]"
                  data-testid="holding-days-slider"
                />
                <div className="flex justify-between items-center text-xs mt-1.5">
                  <span className="text-gray-500">7d</span>
                  <div className="relative">
                    <input
                      type="number"
                      value={holdingStr}
                      min="7"
                      max="365"
                      onChange={(e) => {
                        setHoldingStr(e.target.value);
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v >= 0) setHoldingDays(Math.min(365, v));
                      }}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        const safe = (!isNaN(v) && v >= 7) ? Math.min(365, v) : 90;
                        setHoldingDays(safe);
                        setHoldingStr(String(safe));
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') runSimulation(); }}
                      className="w-20 px-2 py-1 bg-[#0a0a12] border border-[#00ff88]/30 rounded-lg text-xs text-[#00ff88] font-bold text-center focus:outline-none focus:border-[#00ff88]/60"
                      data-testid="holding-days-input"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 pointer-events-none">d</span>
                  </div>
                  <span className="text-gray-500">365d</span>
                </div>
              </div>

              {/* Gross APY — Slider + Text Input + Auto-Fill */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <label className="text-xs text-gray-400">Gross APY</label>
                    <FeatureInfo featureKey="apy_input" size="xs" />
                  </div>
                  <button
                    onClick={autoFillMarketRates}
                    disabled={autoFillLoading}
                    className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-[#00ff88]/10 text-[#00ff88] border border-[#00ff88]/20 text-[10px] font-bold hover:bg-[#00ff88]/20 transition-all disabled:opacity-50"
                    data-testid="auto-fill-apy-btn"
                  >
                    <RefreshCw className={`w-3 h-3 ${autoFillLoading ? 'animate-spin' : ''}`} />
                    {autoFillLoading ? 'Fetching...' : 'Auto-Fill Market Rate'}
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input 
                    type="range" min="1" max="200" step="1"
                    value={grossApy}
                    onChange={(e) => { setGrossApy(Number(e.target.value)); setApyStr(e.target.value); }}
                    className="flex-1 accent-[#00ff88]"
                    data-testid="apy-slider"
                  />
                  <div className="relative flex-shrink-0 w-20">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={apyStr}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^0-9.]/g, '');
                        setApyStr(raw);
                        const v = parseFloat(raw);
                        if (!isNaN(v) && v > 0) setGrossApy(Math.min(200, Math.max(1, v)));
                      }}
                      onBlur={(e) => {
                        const v = parseFloat(e.target.value);
                        const safe = (!isNaN(v) && v >= 1) ? Math.min(200, v) : 50;
                        setGrossApy(safe);
                        setApyStr(String(safe));
                      }}
                      onKeyDown={(e) => { if (e.key === 'Enter') runSimulation(); }}
                      className="w-full px-2 py-1.5 bg-[#0a0a12] border border-[#00ff88]/30 rounded-lg text-xs text-[#00ff88] font-bold text-center focus:outline-none focus:border-[#00ff88]/60"
                      data-testid="apy-input"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 pointer-events-none">%</span>
                  </div>
                </div>
                <p className="text-[10px] text-gray-600 mt-1">
                  {simLinkedPool ? `Sourced from ${simLinkedPool.symbol} — edit freely` : `Type or drag. Click "Auto-Fill" to use live ${selectedChain} market rate.`}
                </p>
              </div>

              {/* Chain Selection */}
              <div className="mb-4">
                <label className="block text-xs text-gray-400 mb-2">Chain
                  <span className="text-[#ffa502] ml-2 font-normal">
                    (gas: ${activeGasEntry.toFixed(2)} in + ${activeGasExit.toFixed(2)} out)
                  </span>
                </label>
                <select 
                  value={selectedChain}
                  onChange={(e) => handleChainChange(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:border-[#00ff88]/50 focus:outline-none"
                  data-testid="chain-select"
                >
                  {chains.map(c => (
                    <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                  ))}
                </select>
              </div>

              {/* Gas Fees (Editable) */}
              <div className="mb-4 p-3 bg-gray-900/40 rounded-lg border border-gray-800">
                <p className="text-[10px] text-gray-500 mb-2 uppercase tracking-wide">
                  Gas Fees — {selectedChain.charAt(0).toUpperCase() + selectedChain.slice(1)}
                  <span className="normal-case text-gray-600 ml-1">(editable)</span>
                  <FeatureInfo featureKey="gas_fees" size="xs" className="ml-1 inline-flex" /></p>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-gray-600">Entry Gas ($)</label>
                    <input
                      type="number"
                      value={gasEntry ?? activeGasEntry}
                      step="0.01"
                      onChange={(e) => setGasEntry(Number(e.target.value))}
                      className="w-full mt-0.5 px-2 py-1 bg-[#0a0a12] border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-[#ffa502]/40"
                      data-testid="gas-entry-input"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-600">Exit Gas ($)</label>
                    <input
                      type="number"
                      value={gasExit ?? activeGasExit}
                      step="0.01"
                      onChange={(e) => setGasExit(Number(e.target.value))}
                      className="w-full mt-0.5 px-2 py-1 bg-[#0a0a12] border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-[#ffa502]/40"
                      data-testid="gas-exit-input"
                    />
                  </div>
                </div>
                <p className="text-[10px] text-gray-700 mt-1">Defaults auto-set by chain. Override for custom scenarios.</p>
              </div>

              {/* TVL Tier / Slippage */}
              <div className="mb-4">
                <div className="flex items-center gap-1.5 mb-2">
                  <label className="text-xs text-gray-400">Pool TVL Tier</label>
                  <FeatureInfo featureKey="tvl_slippage" size="xs" />
                  <span className="text-[#00d4ff] ml-1 font-normal">
                    ({TVL_SLIPPAGE[tvlTier]?.pct}% slippage = ${activeSlippage.toFixed(2)})
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  {Object.entries(TVL_SLIPPAGE).map(([key, val]) => (
                    <button
                      key={key}
                      onClick={() => setTvlTier(key)}
                      className={`py-1.5 rounded-lg text-[10px] font-bold border transition-all ${
                        tvlTier === key
                          ? 'bg-[#00d4ff]/20 text-[#00d4ff] border-[#00d4ff]/40'
                          : 'bg-gray-800/50 text-gray-500 border-gray-700 hover:border-gray-600'
                      }`}
                      data-testid={`tvl-tier-${key}`}
                    >
                      {key.charAt(0).toUpperCase() + key.slice(1)}
                    </button>
                  ))}
                </div>
                <p className="text-[10px] text-gray-600 mt-1">{TVL_SLIPPAGE[tvlTier]?.label}</p>
              </div>

              {/* Stablecoin Toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input 
                  type="checkbox" 
                  checked={isStablecoin}
                  onChange={(e) => setIsStablecoin(e.target.checked)}
                  className="w-4 h-4 accent-[#00ff88]"
                  data-testid="stablecoin-toggle"
                />
                <span className="text-sm text-gray-300 flex items-center gap-1.5">
                  Stablecoin Pool (No IL)
                  <FeatureInfo featureKey="stablecoin_pool" size="xs" />
                </span>
              </label>
            </div>

            {/* Run Simulation Button — right under inputs */}
            <button
              onClick={runSimulation}
              disabled={loading}
              className="w-full py-3.5 bg-gradient-to-r from-[#00ff88]/20 to-[#00d4ff]/10 text-[#00ff88] border border-[#00ff88]/40 rounded-xl text-sm font-bold hover:bg-[#00ff88]/30 transition-all flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="run-simulation-btn"
            >
              {loading
                ? <><RefreshCw className="w-4 h-4 animate-spin" /> Computing...</>
                : <><BarChart3 className="w-4 h-4" /> Run Simulation <kbd className="ml-1 text-[10px] bg-black/30 px-1.5 py-0.5 rounded border border-[#00ff88]/20 text-gray-500 font-mono">↵</kbd></>
              }
            </button>
            {/* Keyboard shortcut hints + Share */}
            <div className="flex items-center justify-between text-[10px] text-gray-600 pt-0.5">
              <div className="flex items-center gap-4">
                <span className="flex items-center gap-1">
                  <kbd className="bg-gray-900 border border-gray-700/60 rounded px-1.5 py-0.5 font-mono text-gray-500">↵</kbd>
                  Run
                </span>
                <span className="text-gray-700">·</span>
                <span className="flex items-center gap-1">
                  <kbd className="bg-gray-900 border border-gray-700/60 rounded px-1.5 py-0.5 font-mono text-gray-500">R</kbd>
                  Reset
                </span>
              </div>
              <button
                onClick={shareSimulation}
                className="flex items-center gap-1 text-gray-500 hover:text-[#00ff88] transition-colors"
                data-testid="share-simulation-btn"
                title="Copy shareable link"
              >
                <Link2 className="w-3 h-3" />
                Share
              </button>
            </div>
            {lastUpdated && (
              <p className="text-[10px] text-center text-gray-600" data-testid="last-updated-label">
                Last updated: {new Date(lastUpdated).toLocaleTimeString()}
              </p>
            )}

            {/* Price Divergence Slider */}
            <div className="neon-card p-4 rounded-xl">
              <div className="flex items-center gap-1.5 mb-3">
                <h3 className="text-sm font-bold text-white">Price Divergence & IL</h3>
                <FeatureInfo featureKey="price_divergence" size="xs" />
              </div>
              <PriceDivergenceSlider 
                value={priceDivergence}
                onChange={setPriceDivergence}
                disabled={isStablecoin}
              />
              
              {/* IL Result */}
              <div className="mt-4 p-3 bg-gray-900/50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs text-gray-400">Impermanent Loss Impact</span>
                    <FeatureInfo featureKey="il_calculator" size="xs" />
                  </div>
                  <span className={`text-lg font-bold ${
                    currentIL > 10 ? 'text-red-400' : currentIL > 3 ? 'text-yellow-400' : 'text-green-400'
                  }`}>
                    {isStablecoin ? '0.00' : currentIL.toFixed(2)}%
                  </span>
                </div>
                <p className="text-[10px] text-gray-500 mt-1">
                  IL = (2√d / (1+d)) - 1 where d = price ratio
                </p>
              </div>
            </div>
          </div>

          {/* Right: Results & Chart */}
          <div className="space-y-4">
              {/* Quick Stats — all computed from same frontend values as Cost Breakdown */}
            <div className="grid grid-cols-2 gap-3">
              <div className="neon-card p-3 rounded-lg" data-testid="stat-gross-apy">
                <div className="flex items-center gap-1 text-[10px] text-gray-500 uppercase">Gross APY <FeatureInfo featureKey="apy_input" size="xs" /></div>
                <div className="text-xl font-bold text-white">{grossApy.toFixed(2)}%</div>
              </div>
              <div className="neon-card p-3 rounded-lg" data-testid="stat-net-apy">
                <div className="flex items-center gap-1 text-[10px] text-gray-500 uppercase">Net APY <FeatureInfo featureKey="net_apy_stat" size="xs" /></div>
                <div className={`text-xl font-bold ${frontendNetAPY > 0 ? 'text-[#00ff88]' : 'text-red-400'}`}>
                  {frontendNetAPY.toFixed(2)}%
                </div>
              </div>
              <div className="neon-card p-3 rounded-lg" data-testid="stat-break-even">
                <div className="flex items-center gap-1 text-[10px] text-gray-500 uppercase">Break-even <FeatureInfo featureKey="breakeven_chart" size="xs" /></div>
                <div className="text-xl font-bold text-[#ffa502]">
                  {frontendBreakEvenDays < 1
                    ? `${(frontendBreakEvenDays * 24).toFixed(1)}h`
                    : `${frontendBreakEvenDays.toFixed(1)}d`
                  }
                </div>
              </div>
              <div className="neon-card p-3 rounded-lg" data-testid="stat-rating">
                <div className="text-[10px] text-gray-500 uppercase">Rating</div>
                <div className={`text-xl font-bold ${
                  frontendRatingScore === 'A' ? 'text-[#00ff88]' :
                  frontendRatingScore === 'B' ? 'text-green-400' :
                  frontendRatingScore === 'C' ? 'text-yellow-400' :
                  frontendRatingScore === 'D' ? 'text-orange-400' : 'text-red-400'
                }`}>
                  {frontendRatingLabel}
                </div>
              </div>
            </div>

            {/* Break-even Chart */}
            <div className="neon-card p-4 rounded-xl relative">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-bold text-white flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-[#00ff88]" />
                  Break-even Visualization
                  <FeatureInfo featureKey="breakeven_chart_section" size="xs" />
                </h3>
                {!isPremium && (
                  <span className="text-[10px] px-2 py-0.5 bg-[#ffa502]/20 text-[#ffa502] rounded font-bold">PRO</span>
                )}
              </div>

              {loading ? (
                <div className="h-64 flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-[#00ff88]" />
                </div>
              ) : (
                <div className="relative">
                  <SimulatorChart 
                    data={chartData}
                    hodlValue={hodlValue}
                    crossoverDay={frontendBreakEvenDays}
                    showHodl={isPremium && !isStablecoin}
                  />
                  
                  {/* Blur gating for HODL line */}
                  {!isPremium && (
                    <div className="absolute top-0 right-0 left-0 h-8 bg-gradient-to-b from-black/80 to-transparent pointer-events-none flex items-start justify-center pt-1">
                      <span className="text-[10px] text-[#ffa502] flex items-center gap-1">
                        <Crown className="w-3 h-3" /> HODL Benchmark requires Pro
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Cost Breakdown */}
            {sim && (
              <div className="neon-card p-4 rounded-xl">
                <h3 className="text-sm font-bold text-white mb-3">Cost Breakdown
                  <span className="ml-2 text-[10px] font-normal text-gray-500 capitalize">{selectedChain}</span>
                  <FeatureInfo featureKey="gas_fees" size="xs" className="ml-2 inline-flex" />
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-gray-400">Entry Gas</span>
                      <span className="text-[10px] text-gray-600 ml-1">(on {selectedChain})</span>
                    </div>
                    <span className="text-red-400">-${activeGasEntry.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-gray-400">Exit Gas</span>
                      <span className="text-[10px] text-gray-600 ml-1">(on {selectedChain})</span>
                    </div>
                    <span className="text-red-400">-${activeGasExit.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <span className="text-gray-400">Slippage</span>
                      <span className="text-[10px] text-gray-600 ml-1">({TVL_SLIPPAGE[tvlTier]?.pct}%)</span>
                    </div>
                    <span className="text-red-400">-${activeSlippage.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Impermanent Loss</span>
                    <span className="text-red-400">-${activeILCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-700 font-bold">
                    <span className="text-white">Total Costs</span>
                    <span className="text-red-400">-${totalEnhancedCosts.toFixed(2)}</span>
                  </div>
                </div>
                <p className="text-[10px] text-gray-700 mt-2">
                  Edit gas fields above to customize for your actual L2 costs.
                </p>
              </div>
            )}

            {/* Yield Projections */}
            {sim && (
              <div className="neon-card p-4 rounded-xl" data-testid="yield-projections">
                <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                  Projected Returns ({holdingDays}d)
                  <FeatureInfo featureKey="projected_returns" size="xs" />
                </h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-400">Gross Yield</span>
                    <div className="text-[#00ff88] font-bold" data-testid="gross-yield">${totalYield60d.toFixed(2)}</div>
                    <span className="text-[10px] text-gray-600">${dailyYield.toFixed(2)}/day</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Net Profit</span>
                    <div className={`font-bold ${frontendNetProfit >= 0 ? 'text-[#00ff88]' : 'text-red-400'}`} data-testid="net-profit">
                      ${frontendNetProfit.toFixed(2)}
                    </div>
                    <span className="text-[10px] text-gray-600">after ${totalEnhancedCosts.toFixed(2)} costs</span>
                  </div>
                </div>
                <p className="text-[10px] text-gray-700 mt-2 border-t border-gray-800 pt-2">
                  Formula: Net Profit = (APY/365 × deposit × days) − (entry gas + exit gas + slippage + IL)
                </p>
              </div>
            )}

            {/* Context footnote */}
            {sim && !simLinkedPool && !poolFromUrl && (
              <div className="flex items-start gap-2 px-3 py-2 bg-gray-900/40 border border-gray-800 rounded-lg" data-testid="sim-default-context-note">
                <Info className="w-3.5 h-3.5 text-gray-500 flex-shrink-0 mt-0.5" />
                <p className="text-[10px] text-gray-500 leading-relaxed">
                  Results based on <span className="text-gray-400">{grossApy}% gross APY</span> on <span className="text-gray-400">{selectedChain.charAt(0).toUpperCase() + selectedChain.slice(1)}</span> with your custom inputs. Link a real pool (top-left) to simulate with live APY data from the Yield Explorer.
                </p>
              </div>
            )}
          </div>
        </div>
          </>
          )
        )}
      </div>
    </div>
  );
}
